import { BreadcrumbGroup } from '@cloudscape-design/components';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCampaign } from '@features/campaigns';

/**
 * Breadcrumb navigation component rendered below the top nav on non-list views.
 * Derives segments from the current route path. Max 3 levels.
 *
 * Requirements: 13.1, 13.2, 13.3, 13.4, 13.5
 */
export function BreadcrumbNav() {
  const location = useLocation();
  const navigate = useNavigate();

  // Resolve the campaign name for campaign detail routes. The hook is disabled
  // when the id is undefined, so this costs nothing on non-campaign routes and
  // reuses the cached config when arriving from elsewhere in the app.
  const campaignId = matchCampaignId(location.pathname);
  const { campaign } = useCampaign(campaignId);

  const segments = buildBreadcrumbs(location.pathname, campaign?.campaignName);

  if (segments.length <= 1) return null; // Don't show on list view (root)

  return (
    <BreadcrumbGroup
      items={segments}
      onFollow={(event) => {
        event.preventDefault();
        navigate(event.detail.href);
      }}
    />
  );
}

interface BreadcrumbItem {
  text: string;
  href: string;
}

/**
 * Returns the campaign id when the path is a campaign detail route, else undefined.
 */
function matchCampaignId(pathname: string): string | undefined {
  const match = pathname.match(/^\/campaigns\/([^/]+)\/?$/);
  return match ? decodeURIComponent(match[1]) : undefined;
}

function buildBreadcrumbs(pathname: string, campaignName?: string): BreadcrumbItem[] {
  // Campaigns tree roots at Campaigns rather than Configurations
  if (pathname === '/campaigns' || pathname.startsWith('/campaigns/')) {
    return buildCampaignBreadcrumbs(pathname, campaignName);
  }

  const items: BreadcrumbItem[] = [{ text: 'Configurations', href: '/' }];

  if (pathname === '/') return items;

  if (pathname === '/configs/new') {
    items.push({ text: 'Create Configuration', href: '/configs/new' });
  } else if (pathname === '/pending-deletions') {
    items.push({ text: 'Pending Deletions', href: '/pending-deletions' });
  } else if (pathname.match(/^\/configs\/[^/]+\/edit$/)) {
    items.push({ text: 'Edit Configuration', href: pathname });
  } else if (pathname.match(/^\/configs\/[^/]+$/)) {
    items.push({ text: 'Configuration Detail', href: pathname });
  }

  return items;
}

function buildCampaignBreadcrumbs(pathname: string, campaignName?: string): BreadcrumbItem[] {
  const items: BreadcrumbItem[] = [{ text: 'Campaigns', href: '/campaigns' }];

  const campaignId = matchCampaignId(pathname);
  if (campaignId) {
    items.push({
      text: campaignName ?? 'Campaign',
      href: `/campaigns/${campaignId}`,
    });
  }

  return items;
}
